"use client";
import React, { createContext, useContext, useState } from "react";
import { BASE_URL } from "../config/apiconfig";

const SettingsContext = createContext();

// ─── helpers ────────────────────────────────────────────────────────────────

const getAuthHeaders = (json = true) => ({
  ...(json && { "Content-Type": "application/json" }),
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const fetchWithTimeout = (url, options = {}, ms = 10_000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return fetch(url, { ...options, signal: controller.signal }).finally(() =>
    clearTimeout(timer)
  );
};

const safeFetch = async (url, options) => {
  try {
    const res = await fetchWithTimeout(url, options);
    return await res.json();
  } catch (err) {
    const message =
      err.name === "AbortError" ? "Request timed out" : "Network error";
    return { status: false, message };
  }
};

// ─── provider ───────────────────────────────────────────────────────────────

export const SettingsProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);

  const withLoading = async (fn) => {
    setLoading(true);
    try {
      return await fn();
    } finally {
      setLoading(false);
    }
  };

  const getProfile = (id) =>
    withLoading(() =>
      safeFetch(`${BASE_URL}/profile/${id}`, { headers: getAuthHeaders() })
    );

  const getSettings = () =>
    withLoading(() =>
      safeFetch(`${BASE_URL}/settings`, { headers: getAuthHeaders() })
    );

  const updateSettings = (id, { title, tax }) =>
    withLoading(() =>
      safeFetch(`${BASE_URL}/settings/${id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title,
          tax,
          update_audit_id: localStorage.getItem("admin_id"),
        }),
      })
    );

  const updateProfile = (id, formData) =>
    withLoading(() =>
      safeFetch(`${BASE_URL}/update-profile/${id}`, {
        method: "POST",
        headers: getAuthHeaders(false), // let browser set multipart boundary
        body: formData,
      })
    );

  const updatePassword = (id, payload) =>
    withLoading(() =>
      safeFetch(`${BASE_URL}/update-password/${id}`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      })
    );

  return (
    <SettingsContext.Provider
      value={{
        loading,
        getProfile,
        getSettings,
        updateProfile,
        updatePassword,
        updateSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);